import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faCalendarAlt,
    faChevronLeft,
    faChevronRight,
    faArrowUp,
    faArrowDown
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import useDistribuidorCalculos from './useDistribuidorCalculos';
import DistribuidorGraficaMetricas from './DistribuidorGraficaMetricas';
import DistribuidorGraficaChart from './DistribuidorGraficaChart';

registerLocale('es', es);

/**
 * Card principal de Órdenes y Métricas Financieras del Distribuidor:
 * - Orquesta la barra superior de filtros de fecha y rango de meses.
 * - Gestiona el estado de visibilidad interactivo de las series de datos.
 * - Integra el panel de métricas e indicadores a la izquierda (DistribuidorGraficaMetricas).
 * - Integra el gráfico interactivo ECharts a la derecha (DistribuidorGraficaChart).
 */
const DistribuidorGraficaCard = ({
    resumen = {},
    historico = [],
    cargando = false,
    mesesRango = 6,
    setMesesRango,
    selectedDate = new Date(),
    onDateChange
}) => {
    const context = useContext(AppContext);
    const isDarkMode = Boolean(
        context?.config?.isDark ||
        context?.isDark ||
        (typeof document !== 'undefined' && document.documentElement.classList.contains('dark')) ||
        (typeof window !== 'undefined' && (localStorage.getItem('isDark') === 'true' || localStorage.getItem('theme') === 'dark'))
    );

    const selectedDateObj = selectedDate || new Date();
    const hoy = new Date();
    const isCurrentMonth =
        selectedDateObj.getFullYear() === hoy.getFullYear() &&
        selectedDateObj.getMonth() === hoy.getMonth();

    const handlePrevMonth = () => {
        const prev = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth() - 1, 1);
        if (onDateChange) {
            onDateChange(prev);
        }
    };

    const handleNextMonth = () => {
        const next = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth() + 1, 1);
        const hoyInicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        if (next <= hoyInicioMes && onDateChange) {
            onDateChange(next);
        }
    };

    const handleDateSelect = (date) => {
        if (onDateChange && date) {
            const nuevoMes = new Date(date.getFullYear(), date.getMonth(), 1);
            onDateChange(nuevoMes);
        }
    };

    // Estados para visibilidad de series en la gráfica
    const [visibleSeries, setVisibleSeries] = useState({
        mesActual: true,
        promedioUltimos3: true,
        promedioAnteriores3: true
    });

    const isAll = visibleSeries.mesActual && visibleSeries.promedioUltimos3 && visibleSeries.promedioAnteriores3;

    const toggleSeries = (key) => {
        setVisibleSeries((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            // Asegurar que al menos una métrica quede visible
            if (!next.mesActual && !next.promedioUltimos3 && !next.promedioAnteriores3) {
                return prev;
            }
            return next;
        });
    };

    const handleToggleAll = () => {
        if (isAll) {
            setVisibleSeries({
                mesActual: true,
                promedioUltimos3: false,
                promedioAnteriores3: false
            });
        } else {
            setVisibleSeries({
                mesActual: true,
                promedioUltimos3: true,
                promedioAnteriores3: true
            });
        }
    };

    // Lógica financiera y series centralizada mediante hook
    const calculos = useDistribuidorCalculos({ resumen, historico, mesesRango });
    const {
        diagnostico,
        categories,
        totalMesValues,
        promedioBloqueValues,
        promedioAnteriores3
    } = calculos;

    return (
        <Card
            className="shadow-sm mb-3 position-relative overflow-hidden"
            style={{
                borderRadius: '12px',
                borderLeft: '4px solid #6366f1'
            }}
        >
            <Card.Body className="p-3">
                {/* 1. Header: Título, Diagnóstico de Salud y Controles de Fecha/Rango */}
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span
                            className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm"
                            style={{ width: '28px', height: '28px', backgroundColor: '#6366f1', fontSize: '0.85rem' }}
                        >
                            <FontAwesomeIcon icon={faChartLine} />
                        </span>
                        <span
                            className="fw-bold mb-0 text-uppercase text-700"
                            style={{ fontSize: '0.80rem', letterSpacing: '0.5px' }}
                        >
                            DINERO GENERADO POR EL DISTRIBUIDOR
                        </span>

                        {/* Badge de Diagnóstico y Salud del Distribuidor */}
                        {!cargando && (
                            <span
                                className={`badge rounded-pill fw-bold ${diagnostico.colorClass}`}
                                style={{
                                    fontSize: '0.72rem',
                                    padding: '4px 8px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                                title={diagnostico.detalle}
                            >
                                <FontAwesomeIcon
                                    icon={diagnostico.esPositivo ? faArrowUp : faArrowDown}
                                    style={{ fontSize: '0.62rem' }}
                                />
                                {diagnostico.texto}
                            </span>
                        )}
                    </div>

                    {/* Selector de Mes Interactivo y Rango 3/6 meses */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div
                            className="d-flex align-items-center rounded-2 px-1 border border-200 bg-100"
                            style={{ height: '28px' }}
                        >
                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-600"
                                onClick={handlePrevMonth}
                                title="Mes anterior"
                                style={{ width: '20px', height: '20px' }}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.65rem' }} />
                            </Button>

                            <div className="d-flex align-items-center px-1">
                                <FontAwesomeIcon
                                    icon={faCalendarAlt}
                                    className="text-primary me-1"
                                    style={{ fontSize: '0.72rem' }}
                                />
                                <DatePicker
                                    selected={selectedDateObj}
                                    onChange={handleDateSelect}
                                    maxDate={new Date()}
                                    dateFormat="MMMM yyyy"
                                    showMonthYearPicker
                                    locale="es"
                                    className="form-control form-control-sm border-0 bg-transparent py-0 px-0 text-center fw-semibold text-1000 text-capitalize"
                                    style={{
                                        width: '110px',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        color: isDarkMode ? '#e2e8f0' : '#1e293b'
                                    }}
                                />
                            </div>

                            <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-600"
                                onClick={handleNextMonth}
                                disabled={isCurrentMonth}
                                title="Mes siguiente"
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    opacity: isCurrentMonth ? 0.3 : 1,
                                    cursor: isCurrentMonth ? 'not-allowed' : 'pointer'
                                }}
                            >
                                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.65rem' }} />
                            </Button>
                        </div>

                        {setMesesRango && (
                            <div className="btn-group btn-group-sm" role="group">
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 fw-semibold ${mesesRango === 3 ? 'btn-primary' : 'btn-falcon-default'}`}
                                    style={{ fontSize: '0.74rem', height: '28px' }}
                                    onClick={() => setMesesRango(3)}
                                >
                                    3 meses
                                </button>
                                <button
                                    type="button"
                                    className={`btn py-0 px-2 fw-semibold ${mesesRango === 6 ? 'btn-primary' : 'btn-falcon-default'}`}
                                    style={{ fontSize: '0.74rem', height: '28px' }}
                                    onClick={() => setMesesRango(6)}
                                >
                                    6 meses
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. Barra de Filtro de Series */}
                <div className="d-flex align-items-center justify-content-end gap-1 mb-2 flex-wrap">
                    <button
                        type="button"
                        onClick={handleToggleAll}
                        className="btn btn-sm py-0 px-2 rounded-pill fw-semibold"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: isDarkMode ? '1px solid #475569' : '1px solid #cbd5e1',
                            backgroundColor: isAll
                                ? (isDarkMode ? '#334155' : '#e2e8f0')
                                : 'transparent',
                            color: isDarkMode ? '#e2e8f0' : '#475569',
                            fontWeight: isAll ? 700 : 500,
                            transition: 'all 0.2s'
                        }}
                        title={isAll ? 'Mostrar solo Promedio Trimestral' : 'Mostrar todas las series en la gráfica'}
                    >
                        Todas
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('mesActual')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #6366f1',
                            backgroundColor: visibleSeries.mesActual ? '#6366f1' : 'transparent',
                            color: visibleSeries.mesActual ? '#ffffff' : '#6366f1',
                            opacity: visibleSeries.mesActual ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar visibilidad del promedio trimestral"
                    >
                        <span>● Promedio Trimestral</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('promedioUltimos3')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #10b981',
                            backgroundColor: visibleSeries.promedioUltimos3 ? '#10b981' : 'transparent',
                            color: visibleSeries.promedioUltimos3 ? '#ffffff' : '#10b981',
                            opacity: visibleSeries.promedioUltimos3 ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar visibilidad de la línea punteada del total pagado del mes"
                    >
                        <span>--- Total Pagado del Mes</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => toggleSeries('promedioAnteriores3')}
                        className="btn btn-sm rounded-pill px-2 py-0 fw-semibold d-flex align-items-center gap-1"
                        style={{
                            fontSize: '0.72rem',
                            height: '24px',
                            border: '1px solid #f59e0b',
                            backgroundColor: visibleSeries.promedioAnteriores3 ? '#f59e0b' : 'transparent',
                            color: visibleSeries.promedioAnteriores3 ? '#ffffff' : '#f59e0b',
                            opacity: visibleSeries.promedioAnteriores3 ? 1 : 0.6,
                            transition: 'all 0.2s'
                        }}
                        title="Alternar línea de referencia del promedio de 3 meses anteriores"
                    >
                        <span>--- Prom. 3 Meses Anteriores</span>
                    </button>
                </div>

                {/* 3. Contenido: Panel de Métricas a la izquierda + Gráfica ECharts a la derecha */}
                <div className="row g-2 align-items-center mt-1">
                    <div className="col-12 col-md-5 col-lg-4">
                        <DistribuidorGraficaMetricas
                            cargando={cargando}
                            promedioUltimos3={calculos.promedioUltimos3}
                            promedioAnteriores3={calculos.promedioAnteriores3}
                            totalMesActual={calculos.totalMesActual}
                            totalMesAnterior={calculos.totalMesAnterior}
                            etiquetaMesActual={calculos.etiquetaMesActual}
                            etiquetaMesAnterior={calculos.etiquetaMesAnterior}
                            variacionMoM={calculos.variacionMoM}
                            esMoMPositivo={calculos.esMoMPositivo}
                            variacionQoQ={calculos.variacionQoQ}
                            esQoQPositivo={calculos.esQoQPositivo}
                            mesesConCompra={calculos.mesesConCompra}
                            visibleSeries={visibleSeries}
                            toggleSeries={toggleSeries}
                            lineColor="#6366f1"
                        />
                    </div>

                    <div className="col-12 col-md-7 col-lg-8 position-relative">
                        <DistribuidorGraficaChart
                            categories={categories}
                            totalMesValues={totalMesValues}
                            promedioBloqueValues={promedioBloqueValues}
                            promedioAnteriores3={promedioAnteriores3}
                            visibleSeries={visibleSeries}
                            isDarkMode={isDarkMode}
                            cargando={cargando}
                        />
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

DistribuidorGraficaCard.propTypes = {
    resumen: PropTypes.object,
    historico: PropTypes.array,
    cargando: PropTypes.bool,
    mesesRango: PropTypes.number,
    setMesesRango: PropTypes.func,
    selectedDate: PropTypes.instanceOf(Date),
    onDateChange: PropTypes.func
};

export default DistribuidorGraficaCard;
